import { useEffect, useState } from "react";
import {
  CalendarDays,
  CirclePlus,
  CreditCard,
  FileText,
  Minus,
  ReceiptText,
} from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { getError } from "@/api";
import { ClientCombobox } from "@/components/ClientCombobox";
import { PdfFileActions } from "@/components/PdfFileActions";
import { ProductCombobox } from "@/components/ProductCombobox";

const initialSale = (searchParams, preparedSale = {}) => ({
  warehouse_id: searchParams.get("warehouse_id") || "",
  motif: searchParams.get("motif") || "vente_directe",
  client: { client_id: "", name: "", phone: "", address: "" },
  amount_paid: "",
  notes: preparedSale.notes || "",
  date_mode: preparedSale.date_mode || "auto",
  movement_date: preparedSale.movement_date || "",
  lines: preparedSale.lines?.map((line) => ({
    product_id: line.product_id,
    quantity: Number(line.quantity),
  })) || [],
});

const emptyLine = { product_id: "", quantity: "" };
const billingChoices = [
  { value: "vente_directe", label: "Vente directe", detail: "Règlement intégral immédiat." },
  { value: "vente_credit", label: "Vente à crédit", detail: "Encaissement partiel ou différé." },
  { value: "don", label: "Don", detail: "Aucun montant encaissé." },
];

export default function SalesPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [sale, setSale] = useState(() => initialSale(
    searchParams,
    location.state?.preparedSale,
  ));
  const [draftLine, setDraftLine] = useState(emptyLine);
  const [savingClient, setSavingClient] = useState(false);

  const load = () => Promise.all([
    api.get("/products"),
    api.get("/warehouses"),
    api.get("/invoices"),
    api.get("/clients"),
  ])
    .then(([productsResponse, warehousesResponse, invoicesResponse, clientsResponse]) => {
      setProducts(productsResponse.data.filter((product) => product.active));
      setWarehouses(warehousesResponse.data);
      setInvoices(invoicesResponse.data);
      setClients(clientsResponse.data);
    })
    .catch((error) => toast.error(getError(error)));

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const motif = searchParams.get("motif");
    const warehouseId = searchParams.get("warehouse_id");
    if (["vente_directe", "vente_credit", "don"].includes(motif) || warehouseId) {
      setSale((current) => ({
        ...current,
        motif: motif || current.motif,
        warehouse_id: warehouseId || current.warehouse_id,
      }));
    }
  }, [searchParams]);

  const productsById = Object.fromEntries(products.map((product) => [product.product_id, product]));
  const saleTotal = sale.lines.reduce((total, line) => {
    const price = Number(productsById[line.product_id]?.sale_price || 0);
    return total + Number(line.quantity || 0) * price;
  }, 0);
  const isDonation = sale.motif === "don";
  const isDirectSale = sale.motif === "vente_directe";
  const mainWarehouse = warehouses.find((warehouse) => warehouse.is_main);

  useEffect(() => {
    if (sale.warehouse_id || !mainWarehouse) {
      return;
    }

    setSale((current) => ({ ...current, warehouse_id: mainWarehouse.warehouse_id }));
  }, [mainWarehouse, sale.warehouse_id]);

  useEffect(() => {
    if (!isDirectSale) {
      return;
    }

    const amountPaid = String(saleTotal);
    setSale((current) => current.amount_paid === amountPaid ? current : {
      ...current,
      amount_paid: amountPaid,
    });
  }, [isDirectSale, saleTotal]);

  useEffect(() => {
    if (!isDonation || sale.amount_paid === "") {
      return;
    }

    setSale((current) => ({ ...current, amount_paid: "" }));
  }, [isDonation, sale.amount_paid]);

  const addLine = () => {
    if (!draftLine.product_id || Number(draftLine.quantity) <= 0) {
      toast.error("Choisissez un produit et une quantité.");
      return;
    }

    setSale((current) => {
      const existingLine = current.lines.find((line) => line.product_id === draftLine.product_id);
      const lines = existingLine
        ? current.lines.map((line) => line.product_id === draftLine.product_id ? {
          ...line,
          quantity: Number(line.quantity) + Number(draftLine.quantity),
        } : line)
        : [
          ...current.lines,
          {
            line_id: `${draftLine.product_id}-${Date.now()}-${current.lines.length}`,
            product_id: draftLine.product_id,
            quantity: Number(draftLine.quantity),
          },
        ];

      return { ...current, lines };
    });
    setDraftLine(emptyLine);
  };

  const removeLine = (lineId) => {
    setSale((current) => ({
      ...current,
      lines: current.lines.filter((line, index) => (
        (line.line_id || `${line.product_id}-${index}`) !== lineId
      )),
    }));
  };

  const addLineOnEnter = (event) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      addLine();
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!sale.lines.length) {
      toast.error("Ajoutez au moins un produit à la facture.");
      return;
    }
    try {
      await api.post("/sales", {
        warehouse_id: sale.warehouse_id,
        motif: sale.motif,
        client: {
          name: sale.client.name,
          phone: sale.client.phone,
          address: sale.client.address,
        },
        amount_paid: Number(sale.amount_paid || 0),
        movement_date: sale.date_mode === "manual" ? sale.movement_date : null,
        notes: sale.notes,
        lines: sale.lines.map(({ product_id, quantity }) => ({ product_id, quantity })),
      });
      toast.success(`Facture générée avec ${sale.lines.length} produit(s).`);
      setSale(initialSale(searchParams));
      setDraftLine(emptyLine);
      load();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const pay = async (invoice) => {
    const value = window.prompt(
      `Paiement pour ${invoice.invoice_number} (solde ${invoice.balance})`,
    );
    if (!value) return;
    try {
      await api.post(`/invoices/${invoice.invoice_id}/payments`, {
        amount: Number(value),
        notes: "Paiement saisi",
      });
      toast.success("Paiement enregistré");
      load();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const manualDate = sale.date_mode === "manual";
  const selectedClient = clients.find((client) => client.client_id === sale.client.client_id);
  const clientCanSave = Boolean(sale.client.name.trim());

  const selectClient = (client) => {
    setSale((current) => ({
      ...current,
      client: client ? {
        client_id: client.client_id,
        name: client.name,
        phone: client.phone,
        address: client.address || "",
      } : { client_id: "", name: "", phone: "", address: "" },
    }));
  };

  const changeClientName = (name) => {
    setSale((current) => ({
      ...current,
      client: { ...current.client, client_id: "", name },
    }));
  };

  const changeClientField = (field, value) => {
    setSale((current) => ({
      ...current,
      client: { ...current.client, [field]: value },
    }));
  };

  const saveClient = async () => {
    if (!clientCanSave) {
      toast.error("Renseignez au moins le nom du client.");
      return;
    }

    setSavingClient(true);
    try {
      const response = await api.post("/clients", {
        name: sale.client.name,
        phone: sale.client.phone,
        address: sale.client.address,
      });
      selectClient(response.data.client);
      setClients((current) => {
        const withoutCurrent = current.filter(
          (client) => client.client_id !== response.data.client.client_id,
        );
        return [...withoutCurrent, response.data.client].sort((left, right) => (
          left.name.localeCompare(right.name, "fr-FR")
        ));
      });
      toast.success(
        response.data.created ? "Client ajouté." : "Ce client existe déjà : il est sélectionné.",
      );
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSavingClient(false);
    }
  };

  const getInvoicePdf = async (documentId) => {
    const response = await api.get(`/documents/${documentId}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  };

  return (
    <div className="page-enter" data-testid="sales-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">VENTES & CRÉDITS</p>
          <h1 data-testid="sales-title">Facturation</h1>
          <p>Créez une vente, un don ou une vente à crédit, puis suivez les règlements.</p>
        </div>
      </div>
      <section className="data-panel" data-testid="sale-create-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">NOUVELLE SORTIE</p>
            <h2>Créer une facture multi-produits</h2>
          </div>
          <ReceiptText size={20} />
        </div>
        <form className="sale-form" data-testid="sale-form" onSubmit={submit}>
          <section className="sale-workflow-section" data-testid="sale-client-section">
            <header>
              <span>1</span>
              <div>
                <p className="eyebrow">CLIENT</p>
                <h3>Informations du client</h3>
              </div>
            </header>
            <div className="sale-section-grid">
              <div className="sale-client-search">
                <ClientCombobox
                  clients={clients}
                  onNameChange={changeClientName}
                  onSelect={selectClient}
                  selectedClientId={sale.client.client_id}
                  value={sale.client.name}
                />
              </div>
              <input
                data-testid="sale-client-name-input"
                disabled={Boolean(selectedClient)}
                onChange={(event) => changeClientField("name", event.target.value)}
                placeholder="Nom complet du client"
                required
                value={sale.client.name}
              />
              <input
                data-testid="sale-client-phone-input"
                disabled={Boolean(selectedClient)}
                onChange={(event) => changeClientField("phone", event.target.value)}
                placeholder="Téléphone (facultatif)"
                value={sale.client.phone}
              />
              <input
                className="sale-client-address-input"
                data-testid="sale-client-address-input"
                disabled={Boolean(selectedClient)}
                onChange={(event) => changeClientField("address", event.target.value)}
                placeholder="Adresse (facultatif)"
                value={sale.client.address}
              />
              <button
                className="secondary-action sale-save-client-button"
                data-testid="sale-save-client-button"
                disabled={savingClient || Boolean(selectedClient && clientCanSave)}
                onClick={saveClient}
                type="button"
              >
                {savingClient ? "Enregistrement…" : "Ajouter le client"}
              </button>
              {selectedClient && (
                <p className="sale-selected-client" data-testid="sale-selected-client">
                  Client sélectionné : <strong>{selectedClient.name}</strong>
                </p>
              )}
            </div>
          </section>

          <section className="sale-workflow-section" data-testid="sale-products-section">
            <header>
              <span>2</span>
              <div>
                <p className="eyebrow">PRODUITS</p>
                <h3>Articles et sous-totaux</h3>
              </div>
            </header>
            <div className="line-adder sale-line-adder" data-testid="sale-line-adder">
              <div data-testid="sale-product-select">
                <ProductCombobox
                  onSelect={(product) => setDraftLine({
                    product_id: product?.product_id || "",
                    quantity: "",
                  })}
                  placeholder="Rechercher un produit pour la facture"
                  products={products}
                  testIdPrefix="sale-product"
                  value={draftLine.product_id}
                />
              </div>
              <input
                data-testid="sale-quantity-input"
                min="0.01"
                onChange={(event) => setDraftLine((current) => ({
                  ...current,
                  quantity: event.target.value,
                }))}
                onKeyDown={addLineOnEnter}
                placeholder="Qté"
                step="0.01"
                type="number"
                value={draftLine.quantity}
              />
              <button
                aria-label="Ajouter le produit à la facture"
                data-testid="add-sale-line-button"
                onClick={addLine}
                title="Ajouter à la facture"
                type="button"
              >
                <CirclePlus size={17} />
              </button>
            </div>
            <div className="sale-lines-review" data-testid="sale-lines-review">
              {!sale.lines.length ? (
                <p className="empty-inline" data-testid="empty-sale-lines-message">
                  Ajoutez un ou plusieurs produits à cette facture.
                </p>
              ) : (
                <div className="line-review-list" data-testid="sale-line-review-list">
                  {sale.lines.map((line, index) => {
                    const lineId = line.line_id || `${line.product_id}-${index}`;
                    const product = productsById[line.product_id];
                    const lineTotal = Number(line.quantity) * Number(product?.sale_price || 0);

                    return (
                      <div data-testid={`sale-line-${lineId}`} key={lineId}>
                        <span>
                          <strong>{product?.name || "Produit"}</strong>
                          <small>
                            {line.quantity}
                            {" × "}
                            {Number(product?.sale_price || 0).toLocaleString("fr-FR")}
                            {" GNF"}
                          </small>
                        </span>
                        <b>{lineTotal.toLocaleString("fr-FR")} GNF</b>
                        <button
                          aria-label="Retirer ce produit de la facture"
                          data-testid={`remove-sale-line-${lineId}`}
                          onClick={() => removeLine(lineId)}
                          type="button"
                        >
                          <Minus size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="sale-total-summary" data-testid="sale-total">
              Total facture : <strong>{saleTotal.toLocaleString("fr-FR")} GNF</strong>
            </p>
          </section>

          <section className="sale-workflow-section" data-testid="sale-billing-type-section">
            <header>
              <span>3</span>
              <div>
                <p className="eyebrow">TYPE DE FACTURATION</p>
                <h3>Choisissez une seule option</h3>
              </div>
            </header>
            <div className="sale-motif-options" data-testid="sale-motif-checkboxes">
              {billingChoices.map((choice) => (
                <label
                  className={sale.motif === choice.value ? "active" : ""}
                  data-testid={`sale-motif-${choice.value}-checkbox`}
                  key={choice.value}
                >
                  <input
                    checked={sale.motif === choice.value}
                    onChange={() => setSale((current) => ({ ...current, motif: choice.value }))}
                    type="checkbox"
                  />
                  <span>
                    <strong>{choice.label}</strong>
                    <small>{choice.detail}</small>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="sale-workflow-section" data-testid="sale-validation-section">
            <header>
              <span>4</span>
              <div>
                <p className="eyebrow">VALIDATION</p>
                <h3>Contrôlez et confirmez l’opération</h3>
              </div>
            </header>
            <div className="sale-section-grid">
              <label className="sale-field-label">
                Dépôt de sortie
                <select
                  data-testid="sale-warehouse-select"
                  onChange={(event) => setSale({ ...sale, warehouse_id: event.target.value })}
                  required
                  value={sale.warehouse_id}
                >
                  <option value="">Sélectionner le dépôt principal</option>
                  {warehouses.filter((warehouse) => warehouse.is_main).map((warehouse) => (
                    <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </label>
              {sale.motif === "vente_credit" ? (
                <label className="sale-field-label">
                  Montant encaissé
                  <input
                    data-testid="sale-amount-paid-input"
                    min="0"
                    onChange={(event) => setSale({ ...sale, amount_paid: event.target.value })}
                    placeholder="Montant encaissé"
                    type="number"
                    value={sale.amount_paid}
                  />
                </label>
              ) : (
                <p className="sale-payment-summary" data-testid="sale-payment-summary">
                  {isDonation
                    ? "Don — montant encaissé : 0 GNF"
                    : `Vente directe — montant encaissé : ${saleTotal.toLocaleString("fr-FR")} GNF`}
                </p>
              )}
              <div className="sale-date-control" data-testid="sale-date-control">
                <span><CalendarDays size={15} /> Date de la sortie</span>
                <div className="movement-date-mode-buttons">
                  <button
                    className={!manualDate ? "active" : ""}
                    data-testid="sale-date-auto-button"
                    onClick={() => setSale({ ...sale, date_mode: "auto", movement_date: "" })}
                    type="button"
                  >
                    Automatique
                  </button>
                  <button
                    className={manualDate ? "active" : ""}
                    data-testid="sale-date-manual-button"
                    onClick={() => setSale({
                      ...sale,
                      date_mode: "manual",
                      movement_date: sale.movement_date || new Date().toISOString().slice(0, 10),
                    })}
                    type="button"
                  >
                    Saisie manuelle
                  </button>
                </div>
                {manualDate && (
                  <input
                    data-testid="sale-manual-date-input"
                    lang="fr-FR"
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(event) => setSale({ ...sale, movement_date: event.target.value })}
                    required
                    type="date"
                    value={sale.movement_date}
                  />
                )}
              </div>
              <label className="sale-field-label sale-notes-field">
                Observation
                <textarea
                  data-testid="sale-notes-input"
                  onChange={(event) => setSale({ ...sale, notes: event.target.value })}
                  placeholder="Observation facultative"
                  value={sale.notes}
                />
              </label>
              <button
                className="primary-action sale-submit-button"
                data-testid="sale-submit-button"
                disabled={!sale.lines.length}
              >
                <CirclePlus size={17} /> Valider la facturation
              </button>
            </div>
          </section>
        </form>
      </section>
      <section className="data-panel products-panel" data-testid="invoices-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">SUIVI CLIENT</p>
            <h2>Factures</h2>
          </div>
          <FileText size={20} />
        </div>
        <div className="responsive-table">
          <table data-testid="invoices-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Client</th>
                <th>Produits</th>
                <th>Total</th>
                <th>Solde</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr data-testid={`invoice-row-${invoice.invoice_id}`} key={invoice.invoice_id}>
                  <td>{invoice.invoice_number}</td>
                  <td>{invoice.client.name}</td>
                  <td>{invoice.lines?.length || 0}</td>
                  <td>{invoice.total.toLocaleString("fr-FR")} GNF</td>
                  <td>{invoice.balance.toLocaleString("fr-FR")} GNF</td>
                  <td>{invoice.status}</td>
                  <td className="sale-invoice-actions">
                    {invoice.document_id && (
                      <PdfFileActions
                        fileName={`${invoice.invoice_number}.pdf`}
                        getPdfBlob={() => getInvoicePdf(invoice.document_id)}
                        testIdPrefix={`invoice-${invoice.invoice_id}`}
                      />
                    )}
                    {invoice.balance > 0 && (
                      <button
                        aria-label={`Enregistrer un paiement pour ${invoice.invoice_number}`}
                        data-testid={`invoice-payment-${invoice.invoice_id}`}
                        onClick={() => pay(invoice)}
                        title="Enregistrer un paiement"
                        type="button"
                      >
                        <CreditCard size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}